import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Camera, X, Loader, Check, RotateCcw, Scan, ChevronDown, Crop, Zap, ZapOff, Smartphone } from 'lucide-react';

// Live detection runs on a downscaled copy of the frame for speed
const DETECTION_MAX_WIDTH = 960;
// Captured stills are capped so warp + OCR stay fast on phones
const STILL_MAX_SIDE = 3000;
const DETECTION_INTERVAL_MS = 200;
// Max movement (in video pixels) for corners to count as stable
const STABILITY_THRESHOLD = 20;
// ~1s of stable, sharp frames before auto-capture
const STABILITY_FRAMES_REQUIRED = 5;
// Sharpness = variance of the Laplacian. A frame is "sharp" when it is above
// this floor and close to the sharpest frame seen recently (focus has settled).
const MIN_SHARPNESS = 25;
const SHARPNESS_RELATIVE = 0.75;
const SHARPNESS_WINDOW = 15;

const AUTO_CAPTURE_KEY = 'scanner_auto_capture';

function readAutoCapturePref() {
  try {
    return localStorage.getItem(AUTO_CAPTURE_KEY) !== 'off';
  } catch {
    return true;
  }
}

// Order corners: top-left, top-right, bottom-right, bottom-left
function orderCorners(corners) {
  const sorted = [...corners].sort((a, b) => a.y - b.y);
  const topTwo = sorted.slice(0, 2).sort((a, b) => a.x - b.x);
  const bottomTwo = sorted.slice(2, 4).sort((a, b) => a.x - b.x);
  return [topTwo[0], topTwo[1], bottomTwo[1], bottomTwo[0]];
}

// Validate quadrilateral has document-like proportions
function validateDocumentShape(corners, imageWidth, imageHeight) {
  const sides = [];
  for (let i = 0; i < 4; i++) {
    const next = (i + 1) % 4;
    sides.push(Math.hypot(corners[next].x - corners[i].x, corners[next].y - corners[i].y));
  }
  // Reject if any side is too short (less than 20% of longest side)
  if (Math.min(...sides) / Math.max(...sides) < 0.2) return { isValid: false, score: 0 };

  const angles = [];
  for (let i = 0; i < 4; i++) {
    const prev = (i + 3) % 4;
    const next = (i + 1) % 4;
    const v1x = corners[prev].x - corners[i].x;
    const v1y = corners[prev].y - corners[i].y;
    const v2x = corners[next].x - corners[i].x;
    const v2y = corners[next].y - corners[i].y;
    const cosAngle = (v1x * v2x + v1y * v2y) / (Math.hypot(v1x, v1y) * Math.hypot(v2x, v2y));
    angles.push(Math.acos(Math.max(-1, Math.min(1, cosAngle))) * 180 / Math.PI);
  }
  // All angles should be between 45 and 135 degrees (rectangular-ish)
  if (angles.some(a => a < 45 || a > 135)) return { isValid: false, score: 0 };

  const area = 0.5 * Math.abs(
    (corners[0].x - corners[2].x) * (corners[1].y - corners[3].y) +
    (corners[1].x - corners[3].x) * (corners[2].y - corners[0].y)
  );
  if (area < imageWidth * imageHeight * 0.15) return { isValid: false, score: 0 };

  // Score: prefer larger, more rectangular shapes
  const angleVariance = angles.reduce((sum, a) => sum + Math.pow(a - 90, 2), 0) / 4;
  const rectangularity = 1 / (1 + angleVariance / 100);
  const areaScore = area / (imageWidth * imageHeight);
  return { isValid: true, score: areaScore * 0.6 + rectangularity * 0.4 };
}

// Find best quadrilateral contour from a binary image
function findBestQuadrilateral(cv, binaryImage, imageWidth, imageHeight) {
  const contours = new cv.MatVector();
  const hierarchy = new cv.Mat();
  cv.findContours(binaryImage, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);

  const imageArea = imageWidth * imageHeight;
  let bestCorners = null;
  let bestScore = 0;

  for (let i = 0; i < contours.size(); i++) {
    const contour = contours.get(i);
    const area = cv.contourArea(contour);
    if (area < imageArea * 0.15 || area > imageArea * 0.95) continue;

    const approx = new cv.Mat();
    cv.approxPolyDP(contour, approx, 0.02 * cv.arcLength(contour, true), true);
    if (approx.rows === 4 && cv.isContourConvex(approx)) {
      const corners = [];
      for (let j = 0; j < 4; j++) {
        corners.push({ x: approx.data32S[j * 2], y: approx.data32S[j * 2 + 1] });
      }
      const validation = validateDocumentShape(corners, imageWidth, imageHeight);
      if (validation.isValid && validation.score > bestScore) {
        bestScore = validation.score;
        bestCorners = corners;
      }
    }
    approx.delete();
  }

  contours.delete();
  hierarchy.delete();
  return bestCorners;
}

/**
 * Detect the document in an image source (video element or canvas).
 * Works on a downscaled copy; returns corners in source pixel coordinates
 * plus the frame's sharpness (variance of the Laplacian).
 */
function analyzeFrame(source, sourceWidth, sourceHeight) {
  const cv = window.cv;
  if (!cv || !cv.Mat || !sourceWidth) return { corners: null, sharpness: 0 };

  const scale = Math.min(1, DETECTION_MAX_WIDTH / sourceWidth);
  const w = Math.round(sourceWidth * scale);
  const h = Math.round(sourceHeight * scale);
  const temp = document.createElement('canvas');
  temp.width = w;
  temp.height = h;
  temp.getContext('2d').drawImage(source, 0, 0, w, h);

  const src = cv.imread(temp);
  const gray = new cv.Mat();
  const blurred = new cv.Mat();
  const edges = new cv.Mat();
  const lap = new cv.Mat();
  const mean = new cv.Mat();
  const std = new cv.Mat();
  const adaptThresh = new cv.Mat();
  const closed = new cv.Mat();
  const kernel = cv.Mat.ones(3, 3, cv.CV_8U);
  const closeKernel = cv.Mat.ones(5, 5, cv.CV_8U);

  try {
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);

    cv.Laplacian(gray, lap, cv.CV_64F);
    cv.meanStdDev(lap, mean, std);
    const sharpness = Math.pow(std.doubleAt(0, 0), 2);

    // Moderate blur — 7×7 is too aggressive and erases edge contrast
    cv.GaussianBlur(gray, blurred, new cv.Size(5, 5), 0);
    // Lower Canny thresholds to catch receipts on similar-colour surfaces
    cv.Canny(blurred, edges, 30, 90);
    // Adaptive threshold catches edges Canny misses under uneven lighting
    cv.adaptiveThreshold(blurred, adaptThresh, 255, cv.ADAPTIVE_THRESH_GAUSSIAN_C, cv.THRESH_BINARY_INV, 11, 2);
    cv.bitwise_or(edges, adaptThresh, edges);
    // Thicken edges, then close to bridge gaps
    cv.dilate(edges, edges, kernel);
    cv.morphologyEx(edges, closed, cv.MORPH_CLOSE, closeKernel);

    const best = findBestQuadrilateral(cv, closed, w, h);
    const corners = best
      ? orderCorners(best).map(c => ({ x: c.x / scale, y: c.y / scale }))
      : null;
    return { corners, sharpness };
  } catch (err) {
    console.error('Detection error:', err);
    return { corners: null, sharpness: 0 };
  } finally {
    [src, gray, blurred, edges, lap, mean, std, adaptThresh, closed, kernel, closeKernel].forEach(m => m.delete());
  }
}

// Perspective-correct the document out of the source canvas
function extractDocument(sourceCanvas, corners) {
  const cv = window.cv;
  if (!cv || !cv.Mat) {
    // Fallback: bounding-box crop with no perspective correction
    const xs = corners.map(c => c.x);
    const ys = corners.map(c => c.y);
    const x0 = Math.max(0, Math.min(...xs));
    const x1 = Math.min(sourceCanvas.width, Math.max(...xs));
    const y0 = Math.max(0, Math.min(...ys));
    const y1 = Math.min(sourceCanvas.height, Math.max(...ys));
    const crop = document.createElement('canvas');
    crop.width = x1 - x0;
    crop.height = y1 - y0;
    crop.getContext('2d').drawImage(sourceCanvas, x0, y0, x1 - x0, y1 - y0, 0, 0, x1 - x0, y1 - y0);
    return crop;
  }

  try {
    const src = cv.imread(sourceCanvas);
    const maxWidth = Math.round(Math.max(
      Math.hypot(corners[1].x - corners[0].x, corners[1].y - corners[0].y),
      Math.hypot(corners[2].x - corners[3].x, corners[2].y - corners[3].y)
    ));
    const maxHeight = Math.round(Math.max(
      Math.hypot(corners[3].x - corners[0].x, corners[3].y - corners[0].y),
      Math.hypot(corners[2].x - corners[1].x, corners[2].y - corners[1].y)
    ));
    const srcPoints = cv.matFromArray(4, 1, cv.CV_32FC2, corners.flatMap(c => [c.x, c.y]));
    const dstPoints = cv.matFromArray(4, 1, cv.CV_32FC2, [
      0, 0,
      maxWidth - 1, 0,
      maxWidth - 1, maxHeight - 1,
      0, maxHeight - 1,
    ]);
    const M = cv.getPerspectiveTransform(srcPoints, dstPoints);
    const dst = new cv.Mat();
    cv.warpPerspective(src, dst, M, new cv.Size(maxWidth, maxHeight));

    const out = document.createElement('canvas');
    out.width = maxWidth;
    out.height = maxHeight;
    cv.imshow(out, dst);

    [src, dst, srcPoints, dstPoints, M].forEach(m => m.delete());
    return out;
  } catch (err) {
    console.error('Perspective transform error:', err);
    return sourceCanvas;
  }
}

// Draw an image source onto a new canvas, capped at STILL_MAX_SIDE
function toCappedCanvas(source, width, height) {
  const scale = Math.min(1, STILL_MAX_SIDE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  canvas.getContext('2d').drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Document Scanner with live edge detection, sharpness-gated auto-capture,
 * full-resolution stills and perspective correction.
 * Falls back to the phone's native camera app via a file input.
 */
function DocumentScanner({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const detectedCornersRef = useRef(null);
  const trackRef = useRef(null);
  const stableCornersRef = useRef(null);
  const stabilityCountRef = useRef(0);
  const sharpnessHistoryRef = useRef([]);
  const autoCapturedRef = useRef(false);
  const streamRef = useRef(null);
  const manualCropContainerRef = useRef(null);
  const draggingCornerRef = useRef(null);
  const capturedImageNaturalRef = useRef(null); // {width, height} of capturedImage
  const headerRef = useRef(null);
  const loupeCanvasRef = useRef(null);
  const loupeImageRef = useRef(null); // pre-loaded Image used for loupe drawImage()
  const nativeInputRef = useRef(null);

  const [cameraStarted, setCameraStarted] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null); // full uncropped still (data URL)
  const [croppedImage, setCroppedImage] = useState(null); // perspective-corrected (data URL)
  const [documentDetected, setDocumentDetected] = useState(false);
  const [documentStable, setDocumentStable] = useState(false);
  const [frameSharp, setFrameSharp] = useState(false);
  const [opencvReady, setOpencvReady] = useState(false);
  const [error, setError] = useState(null);
  const [focusPoint, setFocusPoint] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [showCameraSelector, setShowCameraSelector] = useState(false);
  const [manualCropMode, setManualCropMode] = useState(false);
  const [manualCorners, setManualCorners] = useState(null); // [{x,y}] in container px
  const [activeCorner, setActiveCorner] = useState(null); // index being dragged (for loupe)
  const [autoCapture, setAutoCapture] = useState(readAutoCapturePref);

  // Load OpenCV.js — try local copy first (served from same origin, cached by
  // service worker), fall back to CDN for local development without the file.
  // Cross-origin CDN responses are opaque (status=0) and are never cached by
  // the service worker, causing re-downloads on every PWA session.
  useEffect(() => {
    if (window.cv && window.cv.Mat) {
      setOpencvReady(true);
      return;
    }

    const waitForCv = (onReady, timeoutMs = 20000) => {
      const interval = setInterval(() => {
        if (window.cv && window.cv.Mat) {
          clearInterval(interval);
          clearTimeout(timer);
          onReady(true);
        }
      }, 100);
      const timer = setTimeout(() => {
        clearInterval(interval);
        onReady(false); // timed out — cv unavailable
      }, timeoutMs);
    };

    const loadScript = (src, onSuccess, onFailure) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => waitForCv((ok) => {
        if (ok) onSuccess();
        else onFailure();
      });
      script.onerror = onFailure;
      document.body.appendChild(script);
    };

    // 1st attempt: local file bundled into the Docker image
    loadScript(
      '/opencv.js',
      () => setOpencvReady(true),
      () => {
        console.log('Local opencv.js not found, trying CDN…');
        // 2nd attempt: CDN fallback (dev environment / local build without file)
        loadScript(
          'https://docs.opencv.org/4.9.0/opencv.js',
          () => setOpencvReady(true),
          () => {
            console.warn('OpenCV unavailable — edge detection disabled');
            setOpencvReady(true); // allow camera to work without detection
          }
        );
      }
    );
  }, []);

  // Stop current camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    trackRef.current = null;
    setCameraStarted(false);
  }, []);

  // Start camera with optional device ID
  const startCamera = useCallback(async (deviceId = null) => {
    stopCamera();

    try {
      const constraints = {
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      };

      // Use specific device ID or default to environment-facing camera
      if (deviceId) {
        constraints.video.deviceId = { exact: deviceId };
      } else {
        constraints.video.facingMode = 'environment';
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      const videoTrack = stream.getVideoTracks()[0];
      trackRef.current = videoTrack;

      // Ask for continuous autofocus where the browser exposes focus control
      // (mostly Chrome on Android; iOS Safari ignores this)
      try {
        const caps = videoTrack.getCapabilities?.() || {};
        if (caps.focusMode?.includes('continuous')) {
          await videoTrack.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
        }
      } catch (err) {
        console.log('Continuous focus not available:', err);
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        const currentDeviceId = videoTrack.getSettings().deviceId;
        if (currentDeviceId && !selectedCameraId) {
          setSelectedCameraId(currentDeviceId);
        }
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError('Unable to access the camera. Check permissions, or use your phone camera instead.');
    }
  }, [stopCamera, selectedCameraId]);

  // Enumerate available cameras without opening a second stream.
  // Labels are empty before permission is granted; handleVideoLoad re-enumerates
  // once the main stream has been opened and permission is confirmed.
  useEffect(() => {
    navigator.mediaDevices?.enumerateDevices()
      .then(devices => setCameras(devices.filter(d => d.kind === 'videoinput')))
      .catch(() => {});
  }, []);

  // Filter to get rear/back cameras (environment-facing)
  const rearCameras = cameras.filter(cam => {
    const label = cam.label.toLowerCase();
    return label.includes('back') ||
           label.includes('rear') ||
           label.includes('environment') ||
           label.includes('arrière') ||
           (!label.includes('front') && !label.includes('selfie') && !label.includes('facetime'));
  });

  const getCameraName = (camera, index) => {
    const label = camera.label.toLowerCase();
    if (label.includes('wide') || label.includes('0.5')) return 'Ultra Wide';
    if (label.includes('tele') || label.includes('zoom') || label.includes('2x') || label.includes('3x')) return 'Telephoto';
    if (label.includes('macro')) return 'Macro';
    if (index === 0) return 'Main Camera';
    return `Camera ${index + 1}`;
  };

  // Start camera on mount and when camera selection changes
  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Live camera is not available in this browser. Use your phone camera instead.');
      return undefined;
    }
    startCamera(selectedCameraId);
    return () => {
      stopCamera();
    };
  }, [selectedCameraId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetDetection = useCallback(() => {
    detectedCornersRef.current = null;
    stableCornersRef.current = null;
    stabilityCountRef.current = 0;
    sharpnessHistoryRef.current = [];
    setDocumentDetected(false);
    setDocumentStable(false);
    setFrameSharp(false);
  }, []);

  // Reconnect video stream when returning from preview (retake)
  useEffect(() => {
    if (!capturedImage && videoRef.current && streamRef.current) {
      const timer = setTimeout(() => {
        if (videoRef.current && streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
          videoRef.current.play().catch(err => console.log('Video play error:', err));
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [capturedImage]);

  const handleCameraSelect = useCallback((deviceId) => {
    setSelectedCameraId(deviceId);
    setShowCameraSelector(false);
  }, []);

  const toggleAutoCapture = () => {
    setAutoCapture(prev => {
      const next = !prev;
      try { localStorage.setItem(AUTO_CAPTURE_KEY, next ? 'on' : 'off'); } catch { /* ignore */ }
      return next;
    });
  };

  // Check if corners are similar (stable)
  const cornersAreSimilar = (a, b) => {
    if (!a || !b || a.length !== b.length) return false;
    return a.every((c, i) =>
      Math.abs(c.x - b[i].x) <= STABILITY_THRESHOLD && Math.abs(c.y - b[i].y) <= STABILITY_THRESHOLD
    );
  };

  const drawOverlay = (corners, isReady) => {
    const overlay = overlayCanvasRef.current;
    const video = videoRef.current;
    if (!overlay || !video) return;
    overlay.width = video.videoWidth;
    overlay.height = video.videoHeight;
    const ctx = overlay.getContext('2d');
    ctx.clearRect(0, 0, overlay.width, overlay.height);
    if (!corners) return;

    const strokeColor = isReady ? '#22c55e' : '#eab308';
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 4;
    ctx.fillStyle = isReady ? 'rgba(34, 197, 94, 0.15)' : 'rgba(234, 179, 8, 0.15)';
    ctx.beginPath();
    ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < 4; i++) ctx.lineTo(corners[i].x, corners[i].y);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = strokeColor;
    for (const corner of corners) {
      ctx.beginPath();
      ctx.arc(corner.x, corner.y, 12, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  // Grab the best still available: a full-resolution photo via ImageCapture
  // (Chrome on Android), otherwise the current video frame.
  const grabStill = useCallback(async () => {
    const video = videoRef.current;
    const track = trackRef.current;
    if (typeof window.ImageCapture === 'function' && track) {
      try {
        const blob = await new window.ImageCapture(track).takePhoto();
        const bitmap = await createImageBitmap(blob);
        const canvas = toCappedCanvas(bitmap, bitmap.width, bitmap.height);
        bitmap.close?.();
        return { canvas, fromVideo: false };
      } catch (err) {
        console.log('takePhoto failed, using video frame:', err);
      }
    }
    return { canvas: toCappedCanvas(video, video.videoWidth, video.videoHeight), fromVideo: true };
  }, []);

  // Show a still in the preview: crop it if a document is found
  const showStill = useCallback((canvas, fallbackCorners = null) => {
    let corners = null;
    if (window.cv && window.cv.Mat) {
      corners = analyzeFrame(canvas, canvas.width, canvas.height).corners;
    }
    if (!corners) corners = fallbackCorners;

    setCapturedImage(canvas.toDataURL('image/jpeg', 0.92));
    setCroppedImage(corners ? extractDocument(canvas, corners).toDataURL('image/jpeg', 0.92) : null);
  }, []);

  const captureDocument = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    setProcessing(true);
    try {
      const liveCorners = detectedCornersRef.current;
      const { canvas, fromVideo } = await grabStill();
      // Live corners are in video pixels; only reuse them on a still with
      // the same framing, scaled to the still's resolution
      let fallback = null;
      if (liveCorners && fromVideo) {
        const s = canvas.width / video.videoWidth;
        fallback = liveCorners.map(c => ({ x: c.x * s, y: c.y * s }));
      }
      showStill(canvas, fallback);
    } finally {
      setProcessing(false);
    }
  }, [grabStill, showStill]);

  // Live detection loop
  useEffect(() => {
    if (!cameraStarted || !opencvReady || capturedImage) return undefined;

    let animationId;
    let lastDetection = 0;

    const detectLoop = () => {
      const now = Date.now();
      const video = videoRef.current;
      if (now - lastDetection >= DETECTION_INTERVAL_MS && video && video.videoWidth) {
        lastDetection = now;
        const { corners, sharpness } = analyzeFrame(video, video.videoWidth, video.videoHeight);

        const history = sharpnessHistoryRef.current;
        history.push(sharpness);
        if (history.length > SHARPNESS_WINDOW) history.shift();
        const sharp = sharpness >= MIN_SHARPNESS && sharpness >= Math.max(...history) * SHARPNESS_RELATIVE;

        if (corners) {
          if (cornersAreSimilar(corners, stableCornersRef.current)) {
            stabilityCountRef.current++;
          } else {
            stabilityCountRef.current = 1;
            stableCornersRef.current = corners;
          }
          detectedCornersRef.current = corners;
        } else {
          stabilityCountRef.current = 0;
          stableCornersRef.current = null;
          detectedCornersRef.current = null;
        }

        const stable = Boolean(corners) && stabilityCountRef.current >= STABILITY_FRAMES_REQUIRED;
        drawOverlay(corners, stable && sharp);
        setDocumentDetected(Boolean(corners));
        setDocumentStable(stable);
        setFrameSharp(sharp);
      }
      animationId = requestAnimationFrame(detectLoop);
    };

    detectLoop();
    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [cameraStarted, opencvReady, capturedImage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-capture once the document is held steady and in focus
  useEffect(() => {
    if (!autoCapture || capturedImage || processing || autoCapturedRef.current) return;
    if (documentStable && frameSharp) {
      autoCapturedRef.current = true;
      if (navigator.vibrate) navigator.vibrate(30);
      captureDocument();
    }
  }, [autoCapture, documentStable, frameSharp, capturedImage, processing, captureDocument]);

  const handleVideoLoad = () => {
    setCameraStarted(true);
    // Permission is now granted — re-enumerate to get proper device labels
    navigator.mediaDevices.enumerateDevices()
      .then(devices => setCameras(devices.filter(d => d.kind === 'videoinput')))
      .catch(() => {});
  };

  // Tap-to-focus: focus on the tapped point where supported, then go back to
  // continuous focus. Detection restarts so auto-capture waits for the refocus.
  const handleTapToFocus = useCallback(async (e) => {
    const track = trackRef.current;
    if (!track || !videoRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;

    setFocusPoint({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setTimeout(() => setFocusPoint(null), 1000);
    stabilityCountRef.current = 0;
    sharpnessHistoryRef.current = [];

    try {
      const capabilities = track.getCapabilities?.() || {};
      const modes = capabilities.focusMode || [];
      const mode = modes.includes('single-shot') ? 'single-shot' : modes.includes('manual') ? 'manual' : null;
      if (!mode) return;

      const constraint = { focusMode: mode };
      if ('pointsOfInterest' in capabilities) {
        constraint.pointsOfInterest = [{ x, y }];
      }
      await track.applyConstraints({ advanced: [constraint] });

      if (modes.includes('continuous')) {
        setTimeout(() => {
          track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] })
            .catch(err => console.log('Could not restore continuous focus:', err));
        }, 1500);
      }
    } catch (err) {
      console.log('Focus control not supported or failed:', err);
    }
  }, []);

  // Photo taken with the phone's own camera app (native autofocus)
  const handleNativePhoto = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setProcessing(true);
    const url = URL.createObjectURL(file);
    try {
      const img = await loadImage(url);
      const canvas = toCappedCanvas(img, img.naturalWidth, img.naturalHeight);
      showStill(canvas);
    } catch (err) {
      console.error('Could not read photo:', err);
    } finally {
      URL.revokeObjectURL(url);
      setProcessing(false);
    }
  }, [showStill]);

  const handleConfirm = useCallback(async () => {
    const imageToUse = croppedImage || capturedImage;
    if (!imageToUse) return;

    const blob = await (await fetch(imageToUse)).blob();
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const file = new File([blob], `receipt_${timestamp}.jpg`, { type: 'image/jpeg' });
    onCapture(file, URL.createObjectURL(blob));
  }, [capturedImage, croppedImage, onCapture]);

  const handleRetake = useCallback(() => {
    setCapturedImage(null);
    setCroppedImage(null);
    setManualCropMode(false);
    setManualCorners(null);
    capturedImageNaturalRef.current = null;
    autoCapturedRef.current = false;
    resetDetection();
  }, [resetDetection]);

  const handleClose = useCallback(() => {
    stopCamera();
    onClose();
  }, [stopCamera, onClose]);

  // Push a fake history entry on mount so the phone's back button / swipe-back
  // gesture triggers popstate and closes the scanner instead of leaving the app.
  useEffect(() => {
    window.history.pushState({ scannerOpen: true }, '');
    const onPop = () => handleClose();
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Returns the pixel bounds of the image rendered inside a container with object-contain
  const getRenderedImageBounds = useCallback((container, naturalWidth, naturalHeight) => {
    const cW = container.clientWidth;
    const cH = container.clientHeight;
    const imgAR = naturalWidth / naturalHeight;
    const containerAR = cW / cH;
    let renderW, renderH, offX, offY;
    if (containerAR > imgAR) {
      renderH = cH; renderW = renderH * imgAR;
      offX = (cW - renderW) / 2; offY = 0;
    } else {
      renderW = cW; renderH = renderW / imgAR;
      offX = 0; offY = (cH - renderH) / 2;
    }
    return { renderW, renderH, offX, offY };
  }, []);

  const activateManualCrop = useCallback(() => {
    const img = new Image();
    img.onload = () => {
      capturedImageNaturalRef.current = { width: img.naturalWidth, height: img.naturalHeight };
      loupeImageRef.current = img;
      setManualCropMode(true);
      // Wait one frame for the container to be in the DOM before computing bounds
      requestAnimationFrame(() => {
        const container = manualCropContainerRef.current;
        if (!container) return;
        const { renderW, renderH, offX, offY } = getRenderedImageBounds(
          container, img.naturalWidth, img.naturalHeight
        );
        const m = 0.04;
        // Clamp top-corner Y below the header so handles are always reachable
        const headerH = headerRef.current?.offsetHeight ?? 70;
        const topY   = Math.max(offY + renderH * m, headerH + 8);
        const botY   =          offY + renderH * (1 - m);
        const leftX  =          offX + renderW * m;
        const rightX =          offX + renderW * (1 - m);
        setManualCorners([
          { x: leftX,  y: topY }, // TL
          { x: rightX, y: topY }, // TR
          { x: rightX, y: botY }, // BR
          { x: leftX,  y: botY }, // BL
        ]);
      });
    };
    img.src = capturedImage;
  }, [capturedImage, getRenderedImageBounds]);

  const handleCornerPointerDown = useCallback((e, index) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    draggingCornerRef.current = index;
    setActiveCorner(index);
  }, []);

  const handleOverlayPointerMove = useCallback((e) => {
    if (draggingCornerRef.current === null) return;
    const container = manualCropContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const headerH = headerRef.current?.offsetHeight ?? 70;
    const x = Math.max(0,       Math.min(container.clientWidth,  e.clientX - rect.left));
    const y = Math.max(headerH, Math.min(container.clientHeight, e.clientY - rect.top));
    setManualCorners(prev => {
      const next = [...prev];
      next[draggingCornerRef.current] = { x, y };
      return next;
    });
  }, []);

  const handleOverlayPointerUp = useCallback(() => {
    draggingCornerRef.current = null;
    setActiveCorner(null);
  }, []);

  // Redraw the loupe canvas whenever the active corner moves
  useEffect(() => {
    if (activeCorner === null || !manualCorners || !loupeCanvasRef.current) return;
    const canvas = loupeCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const SIZE = 96;
    const REGION = 50; // image pixels shown in the loupe (~3-4× zoom on a phone)

    const corner = manualCorners[activeCorner];
    const nat = capturedImageNaturalRef.current;
    const container = manualCropContainerRef.current;
    if (!corner || !nat || !container || !loupeImageRef.current) return;

    const { renderW, renderH, offX, offY } = getRenderedImageBounds(container, nat.width, nat.height);
    const imgX = (corner.x - offX) / renderW * nat.width;
    const imgY = (corner.y - offY) / renderH * nat.height;

    ctx.clearRect(0, 0, SIZE, SIZE);
    ctx.drawImage(
      loupeImageRef.current,
      imgX - REGION / 2, imgY - REGION / 2, REGION, REGION,
      0, 0, SIZE, SIZE
    );
    // Crosshair
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(SIZE / 2 - 12, SIZE / 2); ctx.lineTo(SIZE / 2 + 12, SIZE / 2);
    ctx.moveTo(SIZE / 2, SIZE / 2 - 12); ctx.lineTo(SIZE / 2, SIZE / 2 + 12);
    ctx.stroke();
    // Subtle inner ring
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 2, 0, Math.PI * 2);
    ctx.stroke();
  }, [activeCorner, manualCorners, getRenderedImageBounds]);

  const applyManualCrop = useCallback(() => {
    if (!manualCorners || !capturedImageNaturalRef.current) return;
    const container = manualCropContainerRef.current;
    if (!container) return;
    const nat = capturedImageNaturalRef.current;
    const { renderW, renderH, offX, offY } = getRenderedImageBounds(container, nat.width, nat.height);
    // Convert container coordinates → image pixel coordinates
    const imageCorners = manualCorners.map(c => ({
      x: Math.round((c.x - offX) / renderW * nat.width),
      y: Math.round((c.y - offY) / renderH * nat.height),
    }));
    const img = new Image();
    img.onload = () => {
      const srcCanvas = document.createElement('canvas');
      srcCanvas.width = nat.width;
      srcCanvas.height = nat.height;
      srcCanvas.getContext('2d').drawImage(img, 0, 0);
      setCroppedImage(extractDocument(srcCanvas, imageCorners).toDataURL('image/jpeg', 0.92));
      setManualCropMode(false);
      setManualCorners(null);
      capturedImageNaturalRef.current = null;
    };
    img.src = capturedImage;
  }, [manualCorners, capturedImage, getRenderedImageBounds]);

  const nativeCameraInput = (
    <input
      ref={nativeInputRef}
      type="file"
      accept="image/*"
      capture="environment"
      className="hidden"
      onChange={handleNativePhoto}
    />
  );

  if (error && !capturedImage) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-95 z-[80] flex flex-col items-center justify-center p-6">
        {nativeCameraInput}
        <div className="text-center text-white max-w-sm">
          <p className="text-lg mb-6">{error}</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => nativeInputRef.current?.click()}
              disabled={processing}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-xl font-medium disabled:opacity-50"
            >
              {processing ? <Loader className="w-5 h-5 animate-spin" /> : <Smartphone className="w-5 h-5" />}
              Use phone camera
            </button>
            <button
              onClick={handleClose}
              className="px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentCamera = rearCameras.find(cam => cam.deviceId === selectedCameraId);
  const currentCameraIndex = rearCameras.findIndex(cam => cam.deviceId === selectedCameraId);
  const currentCameraName = currentCamera ? getCameraName(currentCamera, currentCameraIndex) : 'Camera';
  const readyToCapture = documentStable && frameSharp;

  let hint;
  if (!opencvReady) hint = 'Loading detection engine…';
  else if (processing) hint = 'Capturing…';
  else if (readyToCapture) hint = autoCapture ? 'Hold still…' : '✓ Ready — tap the shutter';
  else if (documentStable) hint = 'Focusing… tap the receipt to focus';
  else if (documentDetected) hint = 'Hold steady…';
  else hint = 'Frame the receipt on a contrasting surface';

  return (
    <div className="fixed inset-0 bg-black z-[80] flex flex-col">
      {nativeCameraInput}

      {/* Header */}
      <div ref={headerRef} className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/70 to-transparent px-4 pb-4 pt-safe">
        <div className="flex justify-between items-center">
          <button
            onClick={handleClose}
            className="p-2 text-white hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close scanner"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Camera selector (only show if multiple rear cameras) */}
          {rearCameras.length > 1 && !capturedImage ? (
            <div className="relative">
              <button
                onClick={() => setShowCameraSelector(!showCameraSelector)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-full text-white text-sm transition-colors"
              >
                <Camera className="w-4 h-4" />
                <span>{currentCameraName}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showCameraSelector ? 'rotate-180' : ''}`} />
              </button>

              {showCameraSelector && (
                <div className="absolute top-full mt-2 right-0 bg-gray-900/95 backdrop-blur-sm rounded-lg shadow-xl overflow-hidden min-w-[160px] z-20">
                  {rearCameras.map((camera, index) => (
                    <button
                      key={camera.deviceId}
                      onClick={() => handleCameraSelect(camera.deviceId)}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors flex items-center gap-2 ${
                        camera.deviceId === selectedCameraId
                          ? 'bg-blue-600 text-white'
                          : 'text-white hover:bg-white/10'
                      }`}
                    >
                      <Camera className="w-4 h-4" />
                      {getCameraName(camera, index)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-white">
              <Scan className="w-5 h-5" />
              <span className="text-sm font-medium">{capturedImage ? 'Check the scan' : 'Scan receipt'}</span>
            </div>
          )}

          {!capturedImage ? (
            <button
              onClick={toggleAutoCapture}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm transition-colors ${
                autoCapture ? 'bg-yellow-400 text-black' : 'bg-white/20 text-white'
              }`}
              aria-pressed={autoCapture}
              aria-label="Auto-capture"
            >
              {autoCapture ? <Zap className="w-4 h-4" /> : <ZapOff className="w-4 h-4" />}
              Auto
            </button>
          ) : (
            <div className="w-10" />
          )}
        </div>
      </div>

      {/* Close camera selector when tapping elsewhere */}
      {showCameraSelector && (
        <div
          className="absolute inset-0 z-[9]"
          onClick={() => setShowCameraSelector(false)}
        />
      )}

      {/* Camera/Preview View */}
      <div className="flex-1 relative overflow-hidden">
        {!capturedImage ? (
          <>
            {/* Tap-to-focus overlay */}
            <div
              className="absolute inset-0 z-[5]"
              onClick={handleTapToFocus}
            />

            {focusPoint && (
              <div
                className="absolute z-[6] pointer-events-none"
                style={{ left: focusPoint.x - 30, top: focusPoint.y - 30 }}
              >
                <div className="w-[60px] h-[60px] border-2 border-yellow-400 rounded-lg animate-pulse">
                  <div className="absolute top-1/2 left-0 w-2 h-0.5 bg-yellow-400 -translate-y-1/2" />
                  <div className="absolute top-1/2 right-0 w-2 h-0.5 bg-yellow-400 -translate-y-1/2" />
                  <div className="absolute left-1/2 top-0 w-0.5 h-2 bg-yellow-400 -translate-x-1/2" />
                  <div className="absolute left-1/2 bottom-0 w-0.5 h-2 bg-yellow-400 -translate-x-1/2" />
                </div>
              </div>
            )}

            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              onLoadedMetadata={handleVideoLoad}
              className="absolute inset-0 w-full h-full object-cover"
            />

            <canvas
              ref={overlayCanvasRef}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />

            {/* Loading indicator — only blocks on camera, not OpenCV */}
            {!cameraStarted && (
              <div className="absolute inset-0 flex items-center justify-center bg-black">
                <div className="text-center text-white">
                  <Loader className="w-10 h-10 animate-spin mx-auto mb-3" />
                  <p className="text-sm">Starting camera…</p>
                </div>
              </div>
            )}

            {cameraStarted && (
              <div className="absolute bottom-36 left-0 right-0 text-center pointer-events-none px-4">
                <p className={`text-sm px-4 py-2 rounded-full inline-block ${
                  readyToCapture
                    ? 'bg-green-600/80 text-white'
                    : documentDetected
                    ? 'bg-yellow-600/80 text-white'
                    : 'bg-black/50 text-white'
                }`}>
                  {hint}
                </p>
              </div>
            )}
          </>
        ) : (
          <div ref={manualCropContainerRef} className="absolute inset-0">
            <img
              src={manualCropMode ? capturedImage : (croppedImage || capturedImage)}
              alt="Preview"
              className="w-full h-full object-contain bg-gray-900"
            />

            {/* No document found: point the user at Adjust */}
            {!croppedImage && !manualCropMode && (
              <div className="absolute top-20 left-0 right-0 flex justify-center pointer-events-none px-4">
                <p className="bg-black/60 text-white text-sm px-4 py-2 rounded-full text-center">
                  Edges not found — tap Adjust to crop
                </p>
              </div>
            )}

            {/* Loupe magnifier — shown above the active corner while dragging */}
            {manualCropMode && activeCorner !== null && manualCorners && (() => {
              const c = manualCorners[activeCorner];
              const SIZE = 96;
              const cH = manualCropContainerRef.current?.clientHeight ?? 600;
              const cW = manualCropContainerRef.current?.clientWidth  ?? 400;
              // Place above the handle unless it's in the top half (then place below)
              const above = c.y > cH / 2;
              const lx = Math.max(4, Math.min(cW - SIZE - 4, c.x - SIZE / 2));
              const ly = above ? c.y - SIZE - 24 : c.y + 24;
              return (
                <div
                  className="absolute pointer-events-none z-20"
                  style={{
                    left: lx, top: ly,
                    width: SIZE, height: SIZE,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '3px solid #3b82f6',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
                  }}
                >
                  <canvas ref={loupeCanvasRef} width={SIZE} height={SIZE} />
                </div>
              );
            })()}

            {/* Manual crop overlay */}
            {manualCropMode && manualCorners && (
              <svg
                className="absolute inset-0 w-full h-full"
                onPointerMove={handleOverlayPointerMove}
                onPointerUp={handleOverlayPointerUp}
                style={{ touchAction: 'none' }}
              >
                <defs>
                  <mask id="crop-mask">
                    <rect width="100%" height="100%" fill="white" />
                    <polygon
                      points={manualCorners.map(c => `${c.x},${c.y}`).join(' ')}
                      fill="black"
                    />
                  </mask>
                </defs>
                <rect width="100%" height="100%" fill="rgba(0,0,0,0.55)" mask="url(#crop-mask)" />

                <polygon
                  points={manualCorners.map(c => `${c.x},${c.y}`).join(' ')}
                  fill="none"
                  stroke="#60a5fa"
                  strokeWidth="2"
                  strokeDasharray="10 5"
                />

                {manualCorners.map((corner, i) => (
                  <g
                    key={i}
                    onPointerDown={(e) => handleCornerPointerDown(e, i)}
                    style={{ cursor: 'grab', touchAction: 'none' }}
                  >
                    {/* Transparent hit area (easier to tap on mobile) */}
                    <circle cx={corner.x} cy={corner.y} r={30} fill="transparent" />
                    <circle cx={corner.x} cy={corner.y} r={14} fill="white" stroke="#3b82f6" strokeWidth="3" />
                    <line x1={corner.x - 7} y1={corner.y} x2={corner.x + 7} y2={corner.y} stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" />
                    <line x1={corner.x} y1={corner.y - 7} x2={corner.x} y2={corner.y + 7} stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" />
                  </g>
                ))}
              </svg>
            )}

            {manualCropMode && (
              <div className="absolute top-20 left-0 right-0 flex justify-center pointer-events-none">
                <p className="bg-black/60 text-white text-sm px-4 py-2 rounded-full">
                  Drag the corners to fit the document
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Controls */}
      {/* In manual crop mode this becomes a flex-flow item so the image area
          shrinks to the space above it — all 4 corner handles stay reachable.
          In all other modes it floats as an absolute overlay. */}
      <div className={`left-0 right-0 z-[10] px-6 pt-6 pb-safe ${
        manualCropMode
          ? 'relative bg-black'
          : 'absolute bottom-0 bg-gradient-to-t from-black/90 to-transparent'
      }`}>
        <div className="flex justify-center items-center gap-8 pb-2">
          {!capturedImage ? (
            /* ── Camera controls ── */
            <>
              <button
                onClick={(e) => { e.stopPropagation(); nativeInputRef.current?.click(); }}
                className="flex flex-col items-center gap-1 text-white w-16"
              >
                <div className="w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                  <Smartphone className="w-5 h-5" />
                </div>
                <span className="text-[11px] leading-tight text-center">Phone camera</span>
              </button>

              <button
                onClick={(e) => { e.stopPropagation(); captureDocument(); }}
                disabled={!cameraStarted || processing}
                className={`w-20 h-20 rounded-full flex items-center justify-center disabled:opacity-50 transition-all active:scale-95 ring-4 ${
                  readyToCapture
                    ? 'bg-green-500 ring-green-500/30'
                    : 'bg-white ring-white/30'
                }`}
                aria-label="Capture"
              >
                {processing ? (
                  <Loader className={`w-8 h-8 animate-spin ${readyToCapture ? 'text-white' : 'text-gray-800'}`} />
                ) : (
                  <Camera className={`w-8 h-8 ${readyToCapture ? 'text-white' : 'text-gray-800'}`} />
                )}
              </button>

              <div className="w-16" />
            </>
          ) : manualCropMode ? (
            /* ── Manual crop controls ── */
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setManualCropMode(false); setManualCorners(null); }}
                className="flex flex-col items-center gap-1 text-white"
              >
                <div className="w-14 h-14 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                  <X className="w-6 h-6" />
                </div>
                <span className="text-xs">Cancel</span>
              </button>

              <button
                onClick={(e) => { e.stopPropagation(); applyManualCrop(); }}
                className="flex flex-col items-center gap-1 text-white"
              >
                <div className="w-20 h-20 rounded-full bg-blue-500 hover:bg-blue-600 flex items-center justify-center transition-colors ring-4 ring-blue-500/30">
                  <Check className="w-10 h-10" />
                </div>
                <span className="text-xs">Apply crop</span>
              </button>

              <div className="w-14" />
            </>
          ) : (
            /* ── Preview controls ── */
            <>
              <button
                onClick={(e) => { e.stopPropagation(); handleRetake(); }}
                className="flex flex-col items-center gap-1 text-white"
              >
                <div className="w-14 h-14 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                  <RotateCcw className="w-6 h-6" />
                </div>
                <span className="text-xs">Retake</span>
              </button>

              <button
                onClick={(e) => { e.stopPropagation(); handleConfirm(); }}
                className="flex flex-col items-center gap-1 text-white"
              >
                <div className="w-20 h-20 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center transition-colors ring-4 ring-green-500/30">
                  <Check className="w-10 h-10" />
                </div>
                <span className="text-xs">Use</span>
              </button>

              <button
                onClick={(e) => { e.stopPropagation(); activateManualCrop(); }}
                className="flex flex-col items-center gap-1 text-white"
              >
                <div className="w-14 h-14 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                  <Crop className="w-6 h-6" />
                </div>
                <span className="text-xs">Adjust</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentScanner;
